/**
 * Login (JF 2026-09-29): accounts are created by the admin. While there is no account at all, this computer
 * (the server itself) sees a form to create the first admin instead.
 * With Microsoft SSO on, "Log in with Microsoft" is the only way in (JF 2026-10-09: no password login at all). The password
 * form only appears without SSO, i.e. local development on this computer.
 */
import { useState } from "react";
import { useStore } from "../../store.ts";
import { useT } from "../../shared/i18n.ts";
import { api } from "../../shared/api.ts";

/** Why the SSO callback sent the browser back (?sso_error=…); read once, then removed from the address bar. */
function takeSsoError(): string {
  const q = new URLSearchParams(window.location.search);
  const e = q.get("sso_error") || "";
  if (e) { q.delete("sso_error"); const rest = q.toString(); window.history.replaceState(null, "", window.location.pathname + (rest ? "?" + rest : "") + window.location.hash); }
  return e;
}

export function Login({ setup, sso, onDone, title = "Instant Costing" }: { setup: boolean; sso: boolean; onDone(): void; title?: string }) {
  const t = useT();
  const lang = useStore((s) => s.lang);
  const { setLang, setUser } = useStore.getState();
  const [username, setUsername] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [password, setPassword] = useState("");
  const [password2, setPassword2] = useState("");
  const [ssoErr] = useState(takeSsoError);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const pwForm = setup || !sso;

  const ssoMsg: Record<string, string> = {
    no_account: t("Your Microsoft account has no email address — ask IT.", "你的 Microsoft 账号没有邮箱信息——请联系 IT。"),
    disabled: t("Your account is disabled — ask the administrator.", "你的账户已停用——请找管理员。"),
    denied: t("Microsoft login was cancelled or refused.", "Microsoft 登录被取消或拒绝。"),
    expired: t("The login took too long — please try again.", "登录超时——请再试一次。"),
    failed: t("Microsoft login failed — please try again, or ask IT.", "Microsoft 登录失败——请重试，或联系 IT。"),
  };

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setErr("");
    if (setup && password !== password2) { setErr(t("The two passwords differ.", "两次输入的密码不一样。")); return; }
    setBusy(true);
    try {
      const r = setup ? await api.setup({ username, displayName, password }) : await api.login(username, password);
      setUser(r.user); onDone();
    } catch (x) {
      const m = x instanceof Error ? x.message : String(x);
      setErr(/Wrong username/.test(m) ? t("Wrong username or password.", "用户名或密码不对。") : /Too many/.test(m) ? t("Too many attempts — wait 15 minutes.", "尝试次数太多——请 15 分钟后再试。")
        : /Microsoft account/.test(m) ? t("Please log in with your Microsoft account.", "请使用 Microsoft 账号登录。") : m);
    } finally { setBusy(false); }
  }

  return (
    <div className="login">
      <form className="panel" onSubmit={submit} id="loginForm">
        <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 14 }}>
          <h1 style={{ margin: 0, fontSize: 22 }}>{title}</h1><span className="tag">{t("Internal", "内部版")}</span>
          <button type="button" className="btn sm" style={{ marginLeft: "auto" }} onClick={() => setLang(lang === "zh" ? "en" : "zh")}>{lang === "zh" ? "EN" : "中文"}</button>
        </div>
        {setup && <div className="warn" style={{ marginBottom: 12 }}>{t("No account exists yet. Create the administrator account — you can then add accounts for your colleagues in the admin page.",
          "还没有任何账户。先创建管理员账户——之后在「后台」里给同事开账户。")}</div>}
        {sso && !setup && <>
          <a className="btn p" id="ssoGo" href={"/api/auth/sso/login?" + new URLSearchParams({ returnTo: "/" + window.location.search })} style={{ display: "block", textAlign: "center", width: "100%", boxSizing: "border-box" }}>
            {t("Log in with Microsoft", "使用 Microsoft 账号登录")}</a>
          {ssoErr && <div className="lerr" id="ssoErr" style={{ marginTop: 10 }}>{ssoMsg[ssoErr] || ssoMsg.failed}</div>}
        </>}
        {pwForm && <>
          <div className="fl"><label>{t("Username", "用户名")}</label>
            <input className="in" id="lUser" autoComplete="username" autoFocus value={username} onChange={(e) => setUsername(e.target.value)} /></div>
          {setup && <div className="fl"><label>{t("Display name", "显示名")}</label>
            <input className="in" id="lName" value={displayName} onChange={(e) => setDisplayName(e.target.value)} placeholder="e.g. Sophia" /></div>}
          <div className="fl"><label>{t("Password", "密码")}{setup && <span className="dim"> · {t("at least 6 characters", "至少 6 位")}</span>}</label>
            <input className="in" id="lPass" type="password" autoComplete={setup ? "new-password" : "current-password"} value={password} onChange={(e) => setPassword(e.target.value)} /></div>
          {setup && <div className="fl"><label>{t("Password again", "再输一次密码")}</label>
            <input className="in" id="lPass2" type="password" autoComplete="new-password" value={password2} onChange={(e) => setPassword2(e.target.value)} /></div>}
          {err && <div className="lerr" id="loginErr">{err}</div>}
          <button className="btn p" id="lGo" type="submit" disabled={busy || !username || !password} style={{ width: "100%", marginTop: 8 }}>
            {setup ? t("Create the admin account", "创建管理员账户") : t("Log in", "登录")}</button>
        </>}
        {!setup && <div className="dim" style={{ fontSize: 12, marginTop: 10 }}>{t("No account? Ask the administrator.", "没有账户？请找管理员开通。")}</div>}
      </form>
    </div>
  );
}
